import type { Range } from '@tiptap/core';
import Document from '@tiptap/extension-document';
import Mention from '@tiptap/extension-mention';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { Placeholder, UndoRedo } from '@tiptap/extensions';
import { Fragment, Slice } from '@tiptap/pm/model';
import { PluginKey } from '@tiptap/pm/state';
import { EditorContent, type JSONContent, useEditor } from '@tiptap/react';
import { exitSuggestion, type SuggestionProps } from '@tiptap/suggestion';
import { useDebounce } from '@uidotdev/usehooks';
import { FormEvent, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { TriangleAlert } from 'components/icons';
import { Avatar, AvatarFallback } from 'components/ui/avatar';
import { Button } from 'components/ui/button';
import { Popover, PopoverContent } from 'components/ui/popover';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useFindMentionsWithoutAccessQuery } from 'store/api/invoiceComments';
import type { InvoiceCommentMention, InvoiceCommentPerson } from 'types/invoiceComments';
import { cn } from 'utils/cn';
import { getInitials, rotateNameSearchTerm, useLastNameFirst } from 'utils/nameOrder';
import { escapeRsqlValue } from 'utils/rsql';
import { commentDoc, mentionQueryAt, tagName } from './commentTree';
import { TAG_CLASS } from './InvoiceCommentEntry';

const PICKER_ROWS = 6;
const SEARCH_DEBOUNCE_MS = 200;
const MAX_MENTIONS = 50;

/** The tag list's plugin: an exit has to name it, a Mention's own key being a fresh one per editor. */
const TAG_SUGGESTION = new PluginKey('invoiceCommentTag');

/** The transaction meta of a tag's trim or end, which the status says. */
const TAG_CHANGE = 'invoiceCommentTagChange';

type TagChange = { kind: 'trimmed' | 'ended'; label: string };

/** The open @-query: where it stands, the caret's box the list hangs from, and how a pick replaces it. */
type Query = { range: Range; query: string; rect: DOMRect | null; pick: (tag: InvoiceCommentMention) => void };

type Props = {
    invoiceId: number;
    /** The editor's accessible name. */
    label: string;
    submitLabelId: string;
    initialText?: string;
    initialMentions?: InvoiceCommentPerson[];
    /** What was typed before the composer moved to another host, which it starts from instead of the text. */
    initialContent?: JSONContent;
    onContentChange?: (content: JSONContent) => void;
    onCancel: () => void;
    /** Rejects when the save fails, which keeps the text in the composer. */
    onSubmit: (text: string, mentions: InvoiceCommentMention[]) => Promise<unknown>;
};

/**
 * The comment composer: plain text with tags, in which Enter starts a new line and only the button saves. Typing `@`
 * and a letter opens the people list at the caret; a pick writes a tag, a unit the caret steps over, which Backspace
 * shortens a word at a time and, at its last word, turns back into the plain `@` query it came from. The status region
 * under the composer, its description, names each tagged person who could not see the comments and so will not be
 * notified.
 */
export function InvoiceCommentComposer({
    invoiceId,
    label,
    submitLabelId,
    initialText = '',
    initialMentions = [],
    initialContent,
    onContentChange,
    onCancel,
    onSubmit,
}: Props) {
    const intl = useIntl();
    const statusId = useId();
    const listId = useId();
    const [query, setQuery] = useState<Query | null>(null);
    // The highlighted row of the list for one query: a new query starts again at the first person.
    const [highlight, setHighlight] = useState({ query: '', index: 0 });
    const [announcement, setAnnouncement] = useState('');
    const [mentions, setMentions] = useState<InvoiceCommentMention[]>([]);
    const [isEmpty, setIsEmpty] = useState(true);
    const [saving, setSaving] = useState(false);

    // The editor is built once; its key handler and the content callback read the last render through these.
    const keyDown = useRef<(event: KeyboardEvent) => boolean>(() => false);
    const contentChanged = useRef(onContentChange);

    const editor = useEditor({
        autofocus: 'end',
        content: initialContent ?? commentDoc(initialText, initialMentions),
        extensions: [
            Document,
            Paragraph,
            Text,
            UndoRedo,
            Placeholder.configure({
                placeholder: intl.formatMessage({ id: 'invoice-comments.placeholder' }),
                emptyEditorClass:
                    'is-editor-empty before:content-[attr(data-placeholder)] before:text-muted-foreground before:pointer-events-none before:float-left before:h-0',
            }),
            tagExtension.configure({
                suggestion: {
                    pluginKey: TAG_SUGGESTION,
                    char: '@',
                    allowSpaces: true,
                    items: () => [],
                    // The list opens on the composer's own @-query rule, a name holding spaces.
                    findSuggestionMatch: ({ $position }) => {
                        const before = $position.parent.textBetween(0, $position.parentOffset, undefined, '￼');
                        const match = mentionQueryAt(before, before.length);
                        return match
                            ? {
                                  range: { from: $position.start() + match.start, to: $position.pos },
                                  query: match.query,
                                  text: `@${match.query}`,
                              }
                            : null;
                    },
                    render: () => {
                        const show = (props: SuggestionProps) =>
                            setQuery({
                                range: props.range,
                                query: props.query,
                                rect: props.clientRect?.() ?? null,
                                pick: (tag) => props.command(tag),
                            });
                        return {
                            onStart: show,
                            onUpdate: show,
                            onExit: () => setQuery(null),
                            onKeyDown: ({ event }) => keyDown.current(event),
                        };
                    },
                },
            }),
        ],
        editorProps: {
            attributes: {
                role: 'textbox',
                'aria-multiline': 'true',
                'aria-autocomplete': 'list',
                'aria-haspopup': 'listbox',
                'aria-label': label,
                'aria-placeholder': intl.formatMessage({ id: 'invoice-comments.placeholder' }),
                'aria-describedby': statusId,
                class: 'border-input focus-visible:border-ring focus-visible:ring-ring/50 text-fg min-h-16 w-full max-h-[calc(10lh+1rem)] overflow-y-auto rounded-md border bg-transparent px-2.5 py-2 text-base whitespace-pre-wrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-3 md:text-sm',
            },
            // A tag's link only raises the peek card here.
            handleDOMEvents: {
                click: (_view, event) => {
                    if (event.target instanceof Element && event.target.closest('a[data-type="mention"]')) {
                        event.preventDefault();
                    }
                    return false;
                },
            },
            // Pasted text arrives as plain text, a line a paragraph: a pasted `@Name` is not a tag.
            handlePaste: (view, event) => {
                const pasted = event.clipboardData?.getData('text/plain') ?? '';
                const { schema } = view.state;
                const lines = pasted.split(/\r?\n/);
                if (lines.length === 1) {
                    view.dispatch(view.state.tr.insertText(pasted));
                } else {
                    const paragraphs = lines.map((line) =>
                        schema.nodes.paragraph.create(null, line ? schema.text(line) : null)
                    );
                    view.dispatch(view.state.tr.replaceSelection(new Slice(Fragment.from(paragraphs), 1, 1)));
                }
                return true;
            },
        },
        onCreate: ({ editor: created }) => {
            setMentions(tagsOf(created.getJSON()));
            setIsEmpty(created.getText().trim() === '');
        },
        onUpdate: ({ editor: updated, transaction }) => {
            const change: TagChange | undefined = transaction.getMeta(TAG_CHANGE);
            setAnnouncement(
                !change
                    ? ''
                    : change.kind === 'trimmed'
                      ? intl.formatMessage({ id: 'invoice-comments.tag-trimmed' }, { label: change.label })
                      : intl.formatMessage({ id: 'invoice-comments.tag-ended' }, { name: change.label })
            );
            const content = updated.getJSON();
            setMentions(tagsOf(content));
            setIsEmpty(updated.getText().trim() === '');
            contentChanged.current?.(content);
        },
        onBlur: ({ editor: blurred }) => exitSuggestion(blurred.view, TAG_SUGGESTION),
    });

    const debounced = useDebounce(query?.query ?? '', SEARCH_DEBOUNCE_MS);
    const people = useEmployeeSearch(query ? debounced : '');
    const open = query !== null && people.length > 0;
    const highlighted = highlight.query === debounced ? Math.min(highlight.index, people.length - 1) : 0;
    const highlightRow = (index: number) => setHighlight({ query: debounced, index });

    const pick = (person: InvoiceCommentPerson) => {
        query?.pick({ employeeId: person.id, label: tagName(person) });
        setAnnouncement(intl.formatMessage({ id: 'invoice-comments.tag-added' }, { name: person.name }));
    };

    useLayoutEffect(() => {
        contentChanged.current = onContentChange;
        keyDown.current = (event) => {
            if (!open) return false;
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                const step = event.key === 'ArrowDown' ? 1 : -1;
                highlightRow((highlighted + step + people.length) % people.length);
                return true;
            }
            if (event.key === 'Enter') {
                pick(people[highlighted]);
                return true;
            }
            // The list closes on Escape, and the drawer around the composer stays.
            if (event.key === 'Escape') event.stopPropagation();
            return false;
        };
    });

    // A textbox takes no aria-expanded: the list it controls and its highlighted row while the list is open.
    useEffect(() => {
        const element = editor?.view.dom;
        if (!element) return;
        if (open) {
            element.setAttribute('aria-controls', listId);
            element.setAttribute('aria-activedescendant', `${listId}-${highlighted}`);
        } else {
            element.removeAttribute('aria-controls');
            element.removeAttribute('aria-activedescendant');
        }
    }, [editor, open, listId, highlighted]);

    const employeeIds = mentions.map((mention) => mention.employeeId);
    // `data` keeps the last answer while the next one loads: each person's access is their own.
    const { data: withoutAccess = [] } = useFindMentionsWithoutAccessQuery(
        { invoiceId, employeeIds },
        { skip: employeeIds.length === 0 }
    );
    const unreachable = mentions.filter((mention) => withoutAccess.includes(mention.employeeId));

    const anchor = useMemo(() => {
        const rect = query?.rect;
        return rect ? { getBoundingClientRect: () => rect } : undefined;
    }, [query?.rect]);

    const submit = (event: FormEvent) => {
        event.preventDefault();
        if (!editor || isEmpty || saving) return;
        setSaving(true);
        onSubmit(editor.getText({ blockSeparator: '\n' }).trim(), mentions.slice(0, MAX_MENTIONS))
            .catch(() => undefined)
            .finally(() => setSaving(false));
    };

    return (
        <form onSubmit={submit} className="border-primary bg-paper new:rounded-xl relative rounded-lg border p-3">
            <EditorContent editor={editor} />
            <Popover
                open={open}
                onOpenChange={(next, details) => {
                    // A press in the editor moves the caret, which keeps or ends the query itself.
                    const target = details.event?.target;
                    if (next || (target instanceof Node && editor?.view.dom.contains(target))) return;
                    if (editor) exitSuggestion(editor.view, TAG_SUGGESTION);
                }}
            >
                <PopoverContent
                    anchor={anchor}
                    side="bottom"
                    align="start"
                    initialFocus={false}
                    finalFocus={false}
                    // Over the comments drawer, as a list opened in it is.
                    positionerClassName="z-(--z-dropdown)"
                    className="new:p-1.5 w-[300px] gap-0 p-1"
                >
                    <div
                        id={listId}
                        role="listbox"
                        aria-label={intl.formatMessage({ id: 'invoice-comments.people-label' })}
                    >
                        {people.map((person, index) => (
                            <div
                                key={person.id}
                                id={`${listId}-${index}`}
                                role="option"
                                aria-selected={index === highlighted}
                                data-highlighted={index === highlighted ? '' : undefined}
                                className="data-highlighted:bg-accent data-highlighted:text-accent-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-2 text-sm outline-hidden select-none"
                                // The editor keeps the focus, and with it the query the pick replaces.
                                onMouseDown={(event) => event.preventDefault()}
                                onMouseMove={() => highlightRow(index)}
                                onClick={() => pick(person)}
                            >
                                <Avatar size="sm">
                                    <AvatarFallback>{getInitials(person.name)}</AvatarFallback>
                                </Avatar>
                                <span className="min-w-0 truncate">{person.name}</span>
                            </div>
                        ))}
                    </div>
                </PopoverContent>
            </Popover>
            <span role="status" className="sr-only">
                {announcement ||
                    (open ? intl.formatMessage({ id: 'invoice-comments.people-count' }, { count: people.length }) : '')}
            </span>
            <div id={statusId} role="status" className={cn(unreachable.length > 0 && 'mt-2 flex flex-col gap-1')}>
                {unreachable.map((mention) => (
                    <p
                        key={mention.employeeId}
                        className="text-warning-darker new:text-warning-ink m-0 flex items-start gap-1.5 text-xs"
                    >
                        <TriangleAlert aria-hidden="true" className="mt-px size-3.5 shrink-0" />
                        <FormattedMessage id="invoice-comments.no-access-warning" values={{ name: mention.label }} />
                    </p>
                ))}
            </div>
            <div className="mt-3 flex flex-wrap justify-end gap-2">
                <Button type="button" variant="ghost" size="lg" onClick={onCancel}>
                    <FormattedMessage id="cancel" />
                </Button>
                <Button
                    type="submit"
                    {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                    disabled={isEmpty || saving}
                >
                    <FormattedMessage id={submitLabelId} />
                </Button>
            </div>
        </form>
    );
}

/**
 * The tag: an atomic inline node holding the employee and the label written after the `@`, drawn as a saved entry
 * draws it. Backspace right after it takes its last word, and at its last word turns it into the plain text it was
 * picked from, short of its last letter; the change rides on the transaction for the status to say.
 */
const tagExtension = Mention.extend({
    addAttributes() {
        return { employeeId: { default: null }, label: { default: '' } };
    },
    addKeyboardShortcuts() {
        return {
            Backspace: () =>
                this.editor.commands.command(({ tr, state }) => {
                    const { empty, from } = state.selection;
                    const tag = empty ? state.doc.resolve(from).nodeBefore : null;
                    if (tag?.type.name !== this.name) return false;
                    const label: string = tag.attrs.label;
                    const words = label.split(' ');
                    const start = from - tag.nodeSize;
                    if (words.length > 1) {
                        const trimmed = words.slice(0, -1).join(' ');
                        tr.setNodeMarkup(start, undefined, { ...tag.attrs, label: trimmed });
                        tr.setMeta(TAG_CHANGE, { kind: 'trimmed', label: trimmed } satisfies TagChange);
                    } else {
                        tr.replaceWith(start, from, state.schema.text(`@${label}`.slice(0, -1)));
                        tr.setMeta(TAG_CHANGE, { kind: 'ended', label } satisfies TagChange);
                    }
                    return true;
                }),
            // Delete right before a tag takes all of it.
            Delete: () =>
                this.editor.commands.command(({ tr, state }) => {
                    const { empty, from } = state.selection;
                    const tag = empty ? state.doc.resolve(from).nodeAfter : null;
                    if (tag?.type.name !== this.name) return false;
                    tr.delete(from, from + tag.nodeSize);
                    return true;
                }),
        };
    },
}).configure({
    renderText: ({ node }) => `@${node.attrs.label}`,
    renderHTML: ({ node }) => [
        'a',
        { href: `/employee/${node.attrs.employeeId}`, 'data-type': 'mention', class: TAG_CLASS },
        `@${node.attrs.label}`,
    ],
});

/** Each tagged employee once, under the label of their first tag; a second tag of theirs reads as text once saved. */
function tagsOf(content: JSONContent): InvoiceCommentMention[] {
    const tags = new Map<number, string>();
    const visit = (node: JSONContent) => {
        if (node.type === 'mention' && !tags.has(node.attrs?.employeeId)) {
            tags.set(node.attrs?.employeeId, node.attrs?.label);
        }
        node.content?.forEach(visit);
    };
    visit(content);
    return [...tags].map(([employeeId, label]) => ({ employeeId, label }));
}

/** Up to six active employees whose full name holds the query. */
function useEmployeeSearch(query: string): InvoiceCommentPerson[] {
    const lastNameFirst = useLastNameFirst();
    const [search, { data }] = useLazyGetEmployeesListWithFiltersQuery();
    useEffect(() => {
        if (query === '') return;
        const term = escapeRsqlValue(query);
        const reversed = lastNameFirst ? rotateNameSearchTerm(query) : null;
        const filters = reversed
            ? `(fullName=lk=('${term}','${escapeRsqlValue(reversed)}'))`
            : `(fullName=lk='${term}')`;
        void search({ filters, page: 0, pageSize: PICKER_ROWS }, true);
    }, [query, lastNameFirst, search]);
    return useMemo(
        () =>
            query === ''
                ? []
                : (data?.content ?? []).slice(0, PICKER_ROWS).map(({ id, name, firstName, lastName }) => ({
                      id,
                      name,
                      firstName,
                      lastName,
                  })),
        [data, query]
    );
}
